import { useState, useRef, useEffect, useCallback } from 'react';
import { ShieldCheck, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { APIStorage } from '@/services/apiStorage';

interface MFAVerifyProps {
  onVerified: (token: string) => void;
  onCancel?: () => void;
}

type View = 'totp' | 'recovery' | 'email-otp';

const MAX_ATTEMPTS = 5;
const LOCKOUT_SECONDS = 30;
const EMAIL_OTP_COOLDOWN_SECONDS = 60;

export function MFAVerify({ onVerified, onCancel }: MFAVerifyProps) {
  const [view, setView] = useState<View>('totp');
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [recoveryCode, setRecoveryCode] = useState('');
  const [emailOtpDigits, setEmailOtpDigits] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [lockoutDisplay, setLockoutDisplay] = useState('');

  // Email OTP state
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [cooldownDisplay, setCooldownDisplay] = useState('');

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const emailOtpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const api = useRef(new APIStorage()).current;

  // Auto-focus first input on mount and view change
  useEffect(() => {
    if (view === 'totp') {
      inputRefs.current[0]?.focus();
    } else if (view === 'email-otp' && emailOtpSent) {
      emailOtpRefs.current[0]?.focus();
    }
  }, [view, emailOtpSent]);

  // Lockout countdown timer
  useEffect(() => {
    if (!lockoutUntil) {
      setLockoutDisplay('');
      return;
    }

    const tick = () => {
      const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockoutUntil(null);
        setLockoutDisplay('');
        setAttempts(0);
      } else {
        setLockoutDisplay(`Zu viele Versuche. Bitte warten Sie ${remaining} Sekunden.`);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  // Email OTP cooldown timer
  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownDisplay('');
      return;
    }

    const tick = () => {
      const remaining = Math.ceil((cooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setCooldownUntil(null);
        setCooldownDisplay('');
      } else {
        setCooldownDisplay(`Neuen Code senden (${remaining}s)`);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const isLockedOut = lockoutUntil !== null && lockoutUntil > Date.now();
  const isCooldown = cooldownUntil !== null && cooldownUntil > Date.now();

  const handleFailure = useCallback(() => {
    const next = attempts + 1;
    setAttempts(next);
    if (next >= MAX_ATTEMPTS) {
      setLockoutUntil(Date.now() + LOCKOUT_SECONDS * 1000);
      setError('');
    } else {
      setError('Ungültiger Code');
    }
  }, [attempts]);

  const submitTotp = useCallback(async (code: string) => {
    if (isLockedOut || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const result = await api.mfaVerifyTotp(code);
      if (result.success) {
        onVerified(result.token);
      } else {
        handleFailure();
        setDigits(Array(6).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch {
      handleFailure();
      setDigits(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  }, [api, onVerified, handleFailure, isLockedOut, isSubmitting]);

  const handleDigitChange = useCallback((index: number, value: string) => {
    if (isLockedOut) return;

    // Only allow single digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError('');

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5 || (digit && newDigits.every(d => d !== ''))) {
      const code = newDigits.join('');
      if (code.length === 6) {
        void submitTotp(code);
      }
    }
  }, [digits, isLockedOut, submitTotp]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    if (isLockedOut) return;

    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newDigits = Array(6).fill('');
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);
    setError('');

    // Focus last filled input or next empty
    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if full code pasted
    if (pasted.length === 6) {
      void submitTotp(pasted);
    }
  }, [isLockedOut, submitTotp]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
        inputRefs.current[index - 1]?.focus();
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  }, [digits]);

  const submitRecoveryCode = async () => {
    if (isLockedOut || isSubmitting || !recoveryCode.trim()) return;
    setIsSubmitting(true);
    setError('');

    try {
      const result = await api.mfaVerifyRecoveryCode(recoveryCode.trim());
      if (result.success) {
        setRemainingCodes(result.remainingCodes);
        onVerified(result.token);
      } else {
        handleFailure();
      }
    } catch {
      handleFailure();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email OTP handlers
  const sendEmailOtp = async () => {
    if (emailOtpSending || isCooldown) return;
    setEmailOtpSending(true);
    setError('');

    try {
      const result = await api.mfaSendEmailOtp();
      setMaskedEmail(result.maskedEmail);
      setEmailOtpSent(true);
      setCooldownUntil(Date.now() + EMAIL_OTP_COOLDOWN_SECONDS * 1000);
      setEmailOtpDigits(Array(6).fill(''));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Senden des Codes');
    } finally {
      setEmailOtpSending(false);
    }
  };

  const submitEmailOtp = useCallback(async (code: string) => {
    if (isLockedOut || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const result = await api.mfaVerifyEmailOtp(code);
      if (result.success) {
        onVerified(result.token);
      } else {
        handleFailure();
        setEmailOtpDigits(Array(6).fill(''));
        emailOtpRefs.current[0]?.focus();
      }
    } catch {
      handleFailure();
      setEmailOtpDigits(Array(6).fill(''));
      emailOtpRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  }, [api, onVerified, handleFailure, isLockedOut, isSubmitting]);

  const handleEmailOtpDigitChange = useCallback((index: number, value: string) => {
    if (isLockedOut) return;

    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...emailOtpDigits];
    newDigits[index] = digit;
    setEmailOtpDigits(newDigits);
    setError('');

    if (digit && index < 5) {
      emailOtpRefs.current[index + 1]?.focus();
    }

    if (digit && index === 5 || (digit && newDigits.every(d => d !== ''))) {
      const code = newDigits.join('');
      if (code.length === 6) {
        void submitEmailOtp(code);
      }
    }
  }, [emailOtpDigits, isLockedOut, submitEmailOtp]);

  const handleEmailOtpPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    if (isLockedOut) return;

    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newDigits = Array(6).fill('');
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setEmailOtpDigits(newDigits);
    setError('');

    const focusIndex = Math.min(pasted.length, 5);
    emailOtpRefs.current[focusIndex]?.focus();

    if (pasted.length === 6) {
      void submitEmailOtp(pasted);
    }
  }, [isLockedOut, submitEmailOtp]);

  const handleEmailOtpKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!emailOtpDigits[index] && index > 0) {
        const newDigits = [...emailOtpDigits];
        newDigits[index - 1] = '';
        setEmailOtpDigits(newDigits);
        emailOtpRefs.current[index - 1]?.focus();
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      emailOtpRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < 5) {
      emailOtpRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  }, [emailOtpDigits]);

  const switchView = (newView: View) => {
    setView(newView);
    setError('');
    setDigits(Array(6).fill(''));
    setRecoveryCode('');
    setRemainingCodes(null);
    setEmailOtpDigits(Array(6).fill(''));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900 px-4">
      <Card className="max-w-md mx-auto w-full">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-2">
            {view === 'email-otp'
              ? <Mail className="w-8 h-8 text-gray-700 dark:text-gray-300" />
              : <ShieldCheck className="w-8 h-8 text-gray-700 dark:text-gray-300" />
            }
          </div>
          <CardTitle>Zwei-Faktor-Authentifizierung</CardTitle>
          <CardDescription>
            {view === 'totp'
              ? 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein'
              : view === 'recovery'
              ? 'Geben Sie einen Ihrer Recovery Codes ein'
              : 'Verifizierung per E-Mail'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {view === 'totp' ? (
            <>
              {/* TOTP digit inputs */}
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {digits.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleDigitChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    disabled={isSubmitting || isLockedOut}
                    aria-label={`Stelle ${i + 1} von 6`}
                    className={cn(
                      'w-12 h-14 text-center text-2xl font-mono',
                      'rounded-md border border-input bg-background',
                      'ring-offset-background transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      error && 'border-red-500 dark:border-red-400'
                    )}
                  />
                ))}
              </div>

              {/* Error / lockout message */}
              {(error || lockoutDisplay) && (
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {lockoutDisplay || error}
                </p>
              )}

              {isSubmitting && (
                <p className="text-sm text-muted-foreground text-center">
                  Wird geprüft...
                </p>
              )}

              {/* Alternative methods */}
              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => switchView('recovery')}
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors block mx-auto"
                >
                  Recovery Code verwenden
                </button>
                <button
                  type="button"
                  onClick={() => switchView('email-otp')}
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors block mx-auto"
                >
                  Per E-Mail verifizieren
                </button>
              </div>
            </>
          ) : view === 'recovery' ? (
            <>
              {/* Recovery code input */}
              <div className="space-y-3">
                <Input
                  type="text"
                  placeholder="XXXX-XXXX-XXXX"
                  value={recoveryCode}
                  onChange={e => {
                    setRecoveryCode(e.target.value);
                    setError('');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') void submitRecoveryCode();
                  }}
                  disabled={isSubmitting || isLockedOut}
                  className="text-center font-mono text-lg tracking-wider"
                  autoFocus
                />

                <Button
                  onClick={submitRecoveryCode}
                  disabled={isSubmitting || isLockedOut || !recoveryCode.trim()}
                  className="w-full"
                >
                  {isSubmitting ? 'Wird geprüft...' : 'Code bestätigen'}
                </Button>
              </div>

              {/* Error / lockout / remaining codes */}
              {(error || lockoutDisplay) && (
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {lockoutDisplay || error}
                </p>
              )}

              {remainingCodes !== null && (
                <p className="text-sm text-muted-foreground text-center">
                  Verbleibende Recovery Codes: {remainingCodes}
                </p>
              )}

              {/* Back to TOTP */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchView('totp')}
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                >
                  Zurück zur Authenticator-App
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Email OTP */}
              {!emailOtpSent ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Wir senden einen Verifizierungscode an deine hinterlegte Recovery-E-Mail-Adresse.
                  </p>
                  <Button
                    onClick={sendEmailOtp}
                    disabled={emailOtpSending}
                    className="w-full"
                  >
                    {emailOtpSending ? 'Wird gesendet...' : 'Code per E-Mail senden'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Code gesendet an <span className="font-medium text-foreground">{maskedEmail}</span>
                  </p>

                  {/* Email OTP digit inputs */}
                  <div className="flex justify-center gap-2" onPaste={handleEmailOtpPaste}>
                    {emailOtpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={el => { emailOtpRefs.current[i] = el; }}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleEmailOtpDigitChange(i, e.target.value)}
                        onKeyDown={e => handleEmailOtpKeyDown(i, e)}
                        disabled={isSubmitting || isLockedOut}
                        aria-label={`Stelle ${i + 1} von 6`}
                        className={cn(
                          'w-12 h-14 text-center text-2xl font-mono',
                          'rounded-md border border-input bg-background',
                          'ring-offset-background transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          error && 'border-red-500 dark:border-red-400'
                        )}
                      />
                    ))}
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    Der Code ist 5 Minuten gültig.
                  </p>

                  {/* Resend button with cooldown */}
                  <Button
                    variant="outline"
                    onClick={sendEmailOtp}
                    disabled={isCooldown || emailOtpSending}
                    className="w-full"
                  >
                    {cooldownDisplay || 'Neuen Code senden'}
                  </Button>
                </div>
              )}

              {/* Error / lockout message */}
              {(error || lockoutDisplay) && (
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {lockoutDisplay || error}
                </p>
              )}

              {isSubmitting && (
                <p className="text-sm text-muted-foreground text-center">
                  Wird geprüft...
                </p>
              )}

              {/* Back to TOTP */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchView('totp')}
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                >
                  Zurück zur Authenticator-App
                </button>
              </div>
            </>
          )}

          {/* Cancel button */}
          {onCancel && (
            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                onClick={onCancel}
                className="w-full text-muted-foreground"
              >
                Anmeldung abbrechen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
